import type {
  FastTrackQuestion,
  FastTrackQuestionRaw,
} from 'views/FastTrackContentView/types';

const KNOWN_TYPES = new Set([
  'QuestionMultipleChoice',
  'QuestionMultipleChoiceImage',
  'QuestionOther',
  'stepsNormal',
  'stepsValues',
  'QuestionComboLock',
  'DragAndDrop',
  'SideDropdown',
  'QuestionFillInTheBlank',
  'QuestionArrangeTheWords',
  'QuestionMatchTheSentence',
  'QuestionTrueFalse',
]);

function isRawQuestion(item: unknown): item is FastTrackQuestionRaw {
  return (
    typeof item === 'object' &&
    item !== null &&
    'type' in item &&
    KNOWN_TYPES.has((item as { type: unknown }).type as string)
  );
}

function parseQuestion(raw: FastTrackQuestionRaw): FastTrackQuestion {
  const base = {
    question: raw.question,
    UPID: raw.UPID,
    date: raw.date,
    pageNumber: raw.pageNumber,
    correctAnswerText: raw.correctAnswerText,
    incorrectAnswerText: raw.incorrectAnswerText,
  };

  switch (raw.type) {
    case 'QuestionMultipleChoice':
    case 'QuestionOther': {
      const answer = raw.answer.filter((a): a is string => a !== null && a !== '');
      return {
        ...base,
        type: raw.type,
        layout: raw.layout,
        columns: raw.columns,
        answer,
        answersCorrect: raw.answersCorrect.slice(0, answer.length),
      };
    }
    case 'QuestionMultipleChoiceImage': {
      const answer = raw.answer.filter((a): a is string => a !== null && a !== '');
      return {
        ...base,
        type: raw.type,
        layout: raw.layout,
        columns: 1,
        answer,
        answersCorrect: raw.answersCorrect.slice(0, answer.length),
      };
    }
    case 'stepsNormal':
    case 'stepsValues':
      return {
        ...base,
        type: raw.type,
        layout: raw.layout,
        steps: raw.steps.filter((s): s is number => s !== null),
        startNumber: raw.startNumber,
        endNumber: raw.endNumber,
        correctAnswer: raw.correctAnswer,
        main_question_id: raw.main_question_id,
        decimals: raw.decimals,
      };
    case 'QuestionComboLock':
      return {
        ...base,
        type: raw.type,
        layout: raw.layout,
        dialAnswer: raw.dial_answer,
        amountOfDials: raw.amountOfDials,
        correctAnswer: raw.correctAnswer,
      };
    case 'DragAndDrop':
      return {
        ...base,
        type: raw.type,
        answer: raw.answer.filter((a): a is string => a !== null && a !== ''),
        options: raw.options.filter((o): o is string => o !== null && o !== ''),
      };
    case 'SideDropdown':
      return {
        ...base,
        type: raw.type,
        answerOptions: raw.answerOptions,
        options: raw.options,
      };
    case 'QuestionFillInTheBlank':
      return {
        ...base,
        type: raw.type,
        layout: raw.layout,
        answer: raw.answer.filter((a): a is string => a !== null && a !== ''),
      };
    case 'QuestionArrangeTheWords':
      return {
        ...base,
        type: raw.type,
        layout: raw.layout,
        words: raw.words.filter((w): w is string => w !== null && w !== ''),
      };
    case 'QuestionMatchTheSentence':
      return {
        ...base,
        type: raw.type,
        layout: raw.layout,
        left_text_one: raw.left_text_one,
        left_text_two: raw.left_text_two,
        left_text_three: raw.left_text_three,
        left_text_four: raw.left_text_four,
        right_text_one: raw.right_text_one,
        right_text_two: raw.right_text_two,
        right_text_three: raw.right_text_three,
        right_text_four: raw.right_text_four,
        answer_one: raw.answer_one,
        answer_two: raw.answer_two,
        answer_three: raw.answer_three,
        answer_four: raw.answer_four,
        image: raw.image,
      };
    case 'QuestionTrueFalse':
      return {
        ...base,
        type: raw.type,
        answersCorrect: raw.answersCorrect,
      };
  }
}

export function parseFastTrackQuestions(data: unknown): FastTrackQuestion[] {
  if (!Array.isArray(data)) {
    console.warn('parseFastTrackQuestions: expected an array, got', typeof data);
    return [];
  }

  return data.reduce<FastTrackQuestion[]>((acc, item, index) => {
    if (!isRawQuestion(item)) {
      console.warn(`parseFastTrackQuestions: skipping item at index ${index} with unknown type`, item);
      return acc;
    }
    acc.push(parseQuestion(item));
    return acc;
  }, []);
}
